<div class="basiq-consent-page">
  <div class="content-wrapper">
    <h2>Manage your data sharing</h2>
    
    <p class="intro-text">
      When you connect your bank through Dynamoney, we use Basiq (an accredited CDR intermediary) to access your financial data securely.
    </p>

    <div class="section-divider"></div>

    <h3>Understanding the Consumer Data Right (CDR)</h3>
    <p>The Consumer Data Right (CDR) regulates the collection and handling of CDR data in line with privacy safeguards and rules that:</p>
    <ul>
      <li>Ensure your data is managed securely</li>
      <li>Provide you with control over how your data is shared and used</li>
      <li>Allow you to decide what data to share and who can access it</li>
    </ul>

    <div class="info-box">
      <mat-icon>verified_user</mat-icon>
      <div>
        <strong>Accredited Data Recipients (ADRs)</strong>
        <p>Basiq is an Accredited Data Recipient (ADR) approved under the CDR framework to receive and manage consumer data securely. ADRs are required to adhere to strict privacy and security rules, ensuring that your data is used only with your consent.</p>
      </div>
    </div>

    <div class="section-divider"></div>

    <h3>Key Benefits for You</h3>
    <div class="benefits-grid">
      <div class="benefit-card">
        <mat-icon>security</mat-icon>
        <strong>Choice and Control</strong>
        <p>You decide what data to share, how it's used, and who can access it</p>
      </div>
      <div class="benefit-card">
        <mat-icon>settings</mat-icon>
        <strong>Manage Consent</strong>
        <p>View, modify, or revoke your consent at any time</p>
      </div>
      <div class="benefit-card">
        <mat-icon>delete_forever</mat-icon>
        <strong>Data Deletion</strong>
        <p>Request data deletion or de-identification whenever you want</p>
      </div>
    </div>

    <div class="section-divider"></div>

    <h3>How We Use Your Data</h3>
    <p>We may use the data collected under the CDR framework for:</p>
    <ul>
      <li><strong>Personalised Services:</strong> Tailoring financial recommendations to your activities</li>
      <li><strong>Operational Purposes:</strong> Preventing fraud, detecting abuse, and generating analytical insights using de-identified data</li>
      <li><strong>Communication:</strong> Sending updates and notifications aligned with your preferences</li>
    </ul>

    <div class="section-divider"></div>

    <h3>Manage or Stop Sharing Your Data</h3>
    <p>When you give consent, you remain in control. You can easily manage your consent at any time using any of the following methods:</p>
    
    <div class="management-options">
      <div class="option-item">
        <mat-icon>account_balance</mat-icon>
        <span>
          <strong>Through the portal (easiest method):</strong> Log in to your portal and manage your bank connection
          <a [routerLink]="['/basiq/add-requirements']">from here</a>
        </span>
      </div>
      <div class="option-item">
        <mat-icon>email</mat-icon>
        <span>
          <strong>By email:</strong> Contact us at 
          <a href="mailto:support@dynamoney.com"> support&#64;dynamoney.com</a>
        </span>
      </div>
      <div class="option-item">
        <mat-icon>phone</mat-icon>
        <span>
          <strong>By phone:</strong> Call our support team at 1300 001 420
        </span>
      </div>
    </div>

    <div class="section-divider"></div>

    <h3>Data Retention and Deletion</h3>
    <div class="info-box highlight">
      <mat-icon>priority_high</mat-icon>
      <div>
        <strong>You have the right to request data deletion at any time.</strong>
      </div>
    </div>

    <p><strong>Upon withdrawal of consent:</strong></p>
    <ul>
      <li>Your data will be securely deleted or de-identified within 24 hours of your request</li>
      <li>We will ensure that any third-party processors securely erase any shared data</li>
      <li>Redundant data will be destroyed (except where we are required by law to retain it for a longer period)</li>
    </ul>

    <p><strong>De-identification Process:</strong></p>
    <p>De-identification involves removing identifiable information while retaining anonymised data for operational purposes, such as analytics and fraud prevention. This includes:</p>
    <ul>
      <li>Removing your personal information from transactions</li>
      <li>Stripping timestamps and descriptions that reveal specific details</li>
      <li>Aggregating data to ensure anonymity</li>
    </ul>

    <div class="notice-box">
      <mat-icon>access_time</mat-icon>
      <p><strong>Your consent automatically expires 12 months</strong> after you connect unless renewed. We will ensure your data is deleted or de-identified promptly when it is no longer required.</p>
    </div>

    <div class="section-divider"></div>

    <h3>Your Rights Under CDR</h3>
    <div class="rights-list">
      <div class="right-item">
        <mat-icon>check_circle</mat-icon>
        <span>Access and review your consent status at any time</span>
      </div>
      <div class="right-item">
        <mat-icon>check_circle</mat-icon>
        <span>Modify the scope of data you're sharing</span>
      </div>
      <div class="right-item">
        <mat-icon>check_circle</mat-icon>
        <span>Withdraw consent and request immediate data deletion</span>
      </div>
      <div class="right-item">
        <mat-icon>check_circle</mat-icon>
        <span>Request information about how your data is being used</span>
      </div>
      <div class="right-item">
        <mat-icon>check_circle</mat-icon>
        <span>Lodge a complaint if you believe your data rights have been violated</span>
      </div>
    </div>

    <div class="section-divider"></div>

    <h3>Need Help?</h3>
    <p>If you have questions about data sharing, your rights under CDR, or need assistance managing your consent, please contact our support team:</p>
    <div class="contact-info">
      <p><mat-icon>phone</mat-icon> <strong>Phone:</strong> 1300 001 420</p>
      <p><mat-icon>email</mat-icon> <strong>Email:</strong> <a href="mailto:support@dynamoney.com.au">support&#64;dynamoney.com</a></p>
    </div>

    <div class="footer-container">
      <p>
        For more details about how we handle your data, see our
        <a href="/privacy-policy">Privacy Policy</a> and 
        <a href="https://basiq.io/cdr-policy/" target="_blank" rel="noopener">Basiq CDR Policy</a>
      </p>
    </div>
  </div>
</div>